import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "react-router-dom";
import { useAccount, useNetwork, useSwitchNetwork, useConnect } from "wagmi";
import { getMyOsharReservations } from "../../services/osharReservationsApi";
import {
  claimOsharStripeReservation,
  clearPendingOsharWalletMintRecord,
  readPendingOsharWalletMintRecord,
  retryPendingOsharWalletSync,
  syncOsharWalletMint,
} from "../../services/osharRealEstateApi";
import {
  OSHAR_REAL_ESTATE_CHAIN_ID,
  OSHAR_REAL_ESTATE_CONTRACT_ADDRESS,
} from "../../services/web3/contractConfig";
import {
  getChainLabel,
  getNftExplorerUrl,
  getSwitchToNetworkLabel,
  getTxExplorerUrl,
} from "../../config/chainEnv";
import { useAuth } from "../../context/AuthContext";
import {
  getOsharReservationImage,
  getOsharReservationLocalFallback,
} from "../../utils/osharReservationImage";

/** Backend POST /api/oshar-real-estate/reservations/:id/claim (server mint + transfer on configured network). */
const CARD_CLAIM_MINT_ENDPOINT_AVAILABLE = true;

const STATUS_LABELS = {
  checkout_created: "Checkout started",
  paid_unminted: "Paid",
  claimed: "Minted",
  checkout_expired: "Expired",
  cancelled: "Cancelled",
};

const STATUS_RANK = {
  paid_unminted: 0,
  claimed: 1,
  checkout_created: 2,
  checkout_expired: 3,
  cancelled: 4,
};

function rankStatus(status) {
  return STATUS_RANK[status] ?? 99;
}

/** Legacy UI guard: if backend ever sends stripeClaimPriceReady === false, hide claim. Vault list is Mongo-only and omits that field. */
function isStripeClaimPriceBlocked(row) {
  return (
    row?.purchaseMethod === "stripe" &&
    row?.status === "paid_unminted" &&
    row?.stripeClaimPriceReady === false
  );
}

function ts(iso) {
  if (!iso) return 0;
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? 0 : t;
}

export function sortReservationsForPreview(reservations) {
  return [...reservations].sort((a, b) => {
    const dr = rankStatus(a.status) - rankStatus(b.status);
    if (dr !== 0) return dr;
    const timeCmp =
      Math.max(ts(b.paidAt), ts(b.createdAt)) - Math.max(ts(a.paidAt), ts(a.createdAt));
    if (timeCmp !== 0) return timeCmp;
    return String(b.id || "").localeCompare(String(a.id || ""));
  });
}

function formatShortWhen(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
}

function shortRef(value) {
  const v = String(value || "").trim();
  if (!v) return "—";
  if (v.length <= 18) return v;
  return `${v.slice(0, 8)}…${v.slice(-6)}`;
}

/** Treat backend placeholders as missing so we never show "none …" as a wallet. */
function formatWalletShort(addr) {
  const v = String(addr || "").trim();
  if (!v || /^none$/i.test(v)) return null;
  return shortRef(v);
}

function isValidTxHash(h) {
  return /^0x[a-fA-F0-9]{64}$/i.test(String(h || "").trim());
}

function formatPurchaseMethod(pm) {
  if (pm === "wallet_verified") return "Wallet mint";
  if (pm === "stripe") return "Card purchase";
  return pm ? String(pm) : "—";
}

function clientDebugMyOsharEnabled() {
  if (typeof window === "undefined") return false;
  try {
    if (new URLSearchParams(window.location.search).get("debugPurchase") === "1") return true;
    if (window.localStorage?.getItem("treydora:gateway-debug-moshar") === "1") return true;
  } catch {
    return false;
  }
  return false;
}

/** Mirrors backend debug: token IDs and flags only (no secrets). */
function logClientMyOsharReservationsDebug(reservations, label) {
  if (!clientDebugMyOsharEnabled()) return;
  const list = Array.isArray(reservations) ? reservations : [];
  const tokenIds = list
    .map((r) => r?.tokenId)
    .filter((t) => t != null && Number.isFinite(Number(t)))
    .map((t) => Number(t))
    .sort((a, b) => a - b);
  const walletVerifiedCount = list.filter((r) => r?.purchaseMethod === "wallet_verified").length;
  const has100017 = list.some((r) => Number(r?.tokenId) === 100017);
  const txHashPresentCount = list.filter(
    (r) => !!(String(r?.claimTxHash || "").trim() || String(r?.walletSyncTxHash || "").trim())
  ).length;
  const snapshot = list.map((r) => ({
    tokenId: r?.tokenId,
    purchaseMethod: r?.purchaseMethod,
    status: r?.status,
    mintStatus: r?.mintStatus,
    walletVerifiedAt: r?.walletVerifiedAt ? "set" : "null",
    claimTxHash: String(r?.claimTxHash || "").trim() ? "present" : "absent",
    walletSyncTxHash: String(r?.walletSyncTxHash || "").trim() ? "present" : "absent",
  }));
  // eslint-disable-next-line no-console
  console.log(`[MyOsharAccessPanel:${label}]`, {
    total: list.length,
    tokenIds,
    wallet_verified_count: walletVerifiedCount,
    has_token_100017: has100017,
    rows_with_tx_hash: txHashPresentCount,
    snapshot,
  });
}

function explorerTxUrl(hash, chainId = OSHAR_REAL_ESTATE_CHAIN_ID) {
  return getTxExplorerUrl(chainId, hash);
}

function parsePendingWalletTokenIds(rec) {
  if (!rec || typeof rec !== "object") return [];
  const out = [];
  if (Array.isArray(rec.tokenIds)) {
    for (const t of rec.tokenIds) {
      const n = Math.floor(Number(t));
      if (Number.isFinite(n)) out.push(n);
    }
  }
  if (rec.tokenId != null) {
    const n = Math.floor(Number(rec.tokenId));
    if (Number.isFinite(n)) out.push(n);
  }
  return [...new Set(out)];
}

function unitRowMintLabel(u) {
  if (u?.purchaseMethod === "wallet_verified") {
    if (u.status === "claimed") return "Wallet mint · synced on-chain";
    if (u.mintStatus === "pending") return "Wallet mint · pending";
    return `Wallet mint · ${STATUS_LABELS[u.status] || u.status}`;
  }
  return STATUS_LABELS[u.status] || u.status;
}

function explorerNftUrl(contract, tokenId, chainId = OSHAR_REAL_ESTATE_CHAIN_ID) {
  return getNftExplorerUrl(chainId, contract || OSHAR_REAL_ESTATE_CONTRACT_ADDRESS, tokenId);
}

/**
 * Group Stripe unit rows (quantity 1 each, same parentPurchaseId or session) into one vault card with child units.
 */
function groupOsharPurchaseUnits(rows) {
  const owned = rows.filter((r) => {
    if (!r || r.status === "split_parent" || r.hiddenFromDashboard === true) return false;
    if (r.status === "checkout_expired" || r.status === "cancelled") return false;
    if (r.status === "paid_unminted" || r.status === "claimed") return true;
    if (r.purchaseMethod === "wallet_verified" && r.tokenId != null && Number.isFinite(Number(r.tokenId))) {
      if (
        r.walletVerifiedAt ||
        String(r.walletSyncTxHash || "").trim() ||
        r.mintStatus === "minted" ||
        r.mintStatus === "pending"
      ) {
        return true;
      }
    }
    return false;
  });
  const sorted = sortReservationsForPreview(owned);
  const map = new Map();
  for (const r of sorted) {
    const sid = String(r.stripeSessionId || "").trim();
    const tier = String(r.tierSlug || "").trim();
    const pid = r.parentPurchaseId != null ? String(r.parentPurchaseId).trim() : "";
    const key =
      pid && r.purchaseMethod === "stripe"
        ? `parent:${pid}`
        : sid && tier && r.purchaseMethod === "stripe"
          ? `stripe:${sid}:${tier}`
          : `id:${r.id}`;
    if (!map.has(key)) {
      map.set(key, {
        ...r,
        _displayQty: 1,
        _unitRows: [r],
        _groupId: String(r.id),
      });
    } else {
      const agg = map.get(key);
      agg._unitRows.push(r);
      agg._displayQty = agg._unitRows.length;
      if (ts(r.paidAt) > ts(agg.paidAt)) agg.paidAt = r.paidAt;
      const allClaimed = agg._unitRows.every((u) => u.status === "claimed");
      const anyPaid = agg._unitRows.some((u) => u.status === "paid_unminted");
      agg.status = allClaimed ? "claimed" : anyPaid ? "paid_unminted" : agg.status;
    }
  }
  return Array.from(map.values());
}

function partitionRows(rows) {
  const pending = [];
  const closed = [];
  for (const r of rows) {
    if (r.status === "checkout_created") pending.push(r);
    else if (r.status === "checkout_expired" || r.status === "cancelled") closed.push(r);
  }
  return {
    owned: groupOsharPurchaseUnits(rows),
    pending: sortReservationsForPreview(pending),
    closed,
  };
}

function displayTitle(r) {
  return r.metadataName || r.propertyTitle || "Oshar property";
}

function groupMintCounts(r) {
  const units = Array.isArray(r._unitRows) ? r._unitRows : [];
  const total = units.length > 0 ? units.length : 1;
  const minted = units.filter((u) => u.status === "claimed").length;
  return { minted, unclaimed: total - minted, total };
}

/** Short pill label on the main card */
function paidBadgeShort(r) {
  const units = Array.isArray(r._unitRows) && r._unitRows.length > 0 ? r._unitRows : [r];
  const allWallet = units.length > 0 && units.every((u) => u.purchaseMethod === "wallet_verified");
  const { minted, unclaimed, total } = groupMintCounts(r);
  if (total > 1) {
    if (minted === total) return allWallet ? "All wallet minted" : "All minted";
    if (minted === 0) return `${total} units · Unclaimed`;
    return `${minted} minted · ${unclaimed} open`;
  }
  if (r.status === "claimed" && allWallet) return "Wallet minted";
  if (r.status === "claimed") return "Minted";
  if (r.status !== "paid_unminted") return STATUS_LABELS[r.status] || r.status;
  if (CARD_CLAIM_MINT_ENDPOINT_AVAILABLE) return "Ready to claim";
  return "Mint setup pending";
}

function badgeClassForGroup(r) {
  const { minted, total } = groupMintCounts(r);
  if (total <= 1) return r.status === "claimed" ? "is-minted" : "is-paid";
  if (minted === total) return "is-minted";
  if (minted === 0) return "is-paid";
  return "is-partial";
}

/** Subtitle line under meta (e.g. "1 minted · 9 unclaimed") */
function groupMintSummaryLine(r) {
  const { minted, unclaimed, total } = groupMintCounts(r);
  if (total <= 1) return null;
  return `${minted} minted · ${unclaimed} unclaimed`;
}

function OsharThumb({ r }) {
  const row = r._unitRows?.[0] || r;
  const [imgSrc, setImgSrc] = useState(() => getOsharReservationImage(row));
  useEffect(() => {
    setImgSrc(getOsharReservationImage(row));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- row derived from r
  }, [
    r,
    row.id,
    row.tokenId,
    row.metadataImageGatewayUrl,
    row.metadataSnapshot?.imageGatewayUrl,
    row.metadataSnapshot?.image,
    row.imageGatewayUrl,
    row.imageUrl,
    row.imageIpfsUri,
    row.propertyTitle,
    row.metadataName,
    row.metadataStatus,
  ]);
  return (
    <div className="dg-mop-card__thumb">
      <img
        src={imgSrc}
        alt=""
        loading="lazy"
        decoding="async"
        onError={() => setImgSrc(getOsharReservationLocalFallback(row))}
      />
      <div className="dg-mop-card__thumb-veil" aria-hidden />
    </div>
  );
}

function UnitTileThumb({ row }) {
  const [imgSrc, setImgSrc] = useState(() => getOsharReservationImage(row));
  useEffect(() => {
    setImgSrc(getOsharReservationImage(row));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- row identity drives image
  }, [
    row?.id,
    row?.tokenId,
    row?.metadataImageGatewayUrl,
    row?.metadataSnapshot?.imageGatewayUrl,
    row?.metadataSnapshot?.image,
    row?.imageGatewayUrl,
    row?.imageUrl,
    row?.imageIpfsUri,
    row?.propertyTitle,
    row?.metadataName,
    row?.metadataStatus,
  ]);
  return (
    <div className="dg-mop-unit-tile__thumb">
      <img src={imgSrc} alt="" loading="lazy" decoding="async" onError={() => setImgSrc(getOsharReservationLocalFallback(row))} />
    </div>
  );
}

const WALLET_UNITS_PREVIEW_MAX = 2;

function WalletMintedUnitsSubsection({ units, onViewUnits, showViewUnits }) {
  const walletUnits = units.filter((u) => u.purchaseMethod === "wallet_verified");
  if (walletUnits.length === 0) return null;
  const shown = walletUnits.slice(0, WALLET_UNITS_PREVIEW_MAX);
  const more = walletUnits.length - shown.length;
  return (
    <div className="dg-mop-card__wallet-units">
      <div className="dg-mop-card__wallet-units-h">Wallet minted units</div>
      <ul className="dg-mop-card__wallet-units-list">
        {shown.map((u) => {
          const tid = u.tokenId;
          const c = String(u.walletSyncContractAddress || "").trim();
          const nft = u.status === "claimed" ? explorerNftUrl(c, tid) : null;
          const txRaw = String(u.walletSyncTxHash || u.claimTxHash || "").trim();
          const txHref = isValidTxHash(txRaw) ? explorerTxUrl(txRaw) : null;
          const walletShort = formatWalletShort(u.claimedWallet);
          const ms = String(u.mintStatus || "").trim();
          const msShow = ms && !/^none$/i.test(ms) ? ms : null;
          return (
            <li key={u.id} className="dg-mop-card__wallet-units-item">
              <div className="dg-mop-card__wallet-units-row1">
                <span className="dg-mop-card__wallet-units-tok">
                  Token #{tid}
                  {msShow ? <span className="dg-mop-card__wallet-units-ms"> · {msShow}</span> : null}
                </span>
                {nft ? (
                  <a href={nft} className="dg-mop-card__wallet-units-link" target="_blank" rel="noreferrer">
                    View on explorer
                  </a>
                ) : (
                  <span className="dg-mop-card__wallet-units-sepolia-muted">Explorer link when minted</span>
                )}
              </div>
              <div className="dg-mop-card__wallet-units-kv">
                {walletShort ? (
                  <div className="dg-mop-card__wallet-units-line" title={String(u.claimedWallet || "").trim()}>
                    <span className="dg-mop-card__wallet-units-k">Wallet:</span>
                    <span className="dg-mop-card__wallet-units-v">{walletShort}</span>
                  </div>
                ) : (
                  <div className="dg-mop-card__wallet-units-line dg-mop-card__wallet-units-line--muted">
                    <span className="dg-mop-card__wallet-units-k">Wallet:</span>
                    <span className="dg-mop-card__wallet-units-v">Wallet unavailable</span>
                  </div>
                )}
                {txHref ? (
                  <div className="dg-mop-card__wallet-units-line" title={txRaw}>
                    <span className="dg-mop-card__wallet-units-k">Tx:</span>
                    <a className="dg-mop-card__wallet-units-v dg-mop-card__wallet-units-v--link" href={txHref} target="_blank" rel="noreferrer">
                      {shortRef(txRaw)}
                    </a>
                  </div>
                ) : txRaw && !isValidTxHash(txRaw) ? null : (
                  <div className="dg-mop-card__wallet-units-line dg-mop-card__wallet-units-line--muted">
                    <span className="dg-mop-card__wallet-units-k">Tx:</span>
                    <span className="dg-mop-card__wallet-units-v">Tx pending</span>
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ul>
      {more > 0 ? (
        <p className="dg-mop-card__wallet-units-more" role="status">
          + {more} more unit{more !== 1 ? "s" : ""}
        </p>
      ) : null}
      {showViewUnits ? (
        <div className="dg-mop-card__wallet-units-footer">
          <button type="button" className="dg-btn dg-btn--tiny dg-btn--ghost" onClick={onViewUnits}>
            View units
          </button>
        </div>
      ) : null}
    </div>
  );
}

function OsharPropertyUnitsModal({ group, onClose, onStripeClaim, claimBusyId, claimErrorsMap }) {
  const { chain } = useNetwork();
  const { address, isConnected } = useAccount();
  const { connectAsync, connectors } = useConnect();
  const units = useMemo(() => {
    const u = Array.isArray(group._unitRows) ? [...group._unitRows] : [group];
    return u.sort((a, b) => (Number(a.unitIndex) || 0) - (Number(b.unitIndex) || 0));
  }, [group]);

  const { minted, unclaimed, total } = groupMintCounts(group);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const title = displayTitle(group);
  const sub = `${total} NFT property unit${total !== 1 ? "s" : ""}`;
  const hasStripe = units.some((u) => u.purchaseMethod === "stripe");
  const hasWallet = units.some((u) => u.purchaseMethod === "wallet_verified");

  return createPortal(
    <div
      className="dg-mop-modal-overlay"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="dg-mop-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="dg-mop-units-modal-title"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="dg-mop-modal__head">
          <div>
            <h3 id="dg-mop-units-modal-title" className="dg-mop-modal__title serif">
              {title}
            </h3>
            <p className="dg-mop-modal__sub">{sub}</p>
          </div>
          <button type="button" className="dg-mop-modal__close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>
        <div className="dg-mop-modal__chips">
          <span className="dg-mop-modal__chip">{minted} minted</span>
          <span className="dg-mop-modal__chip">{unclaimed} unclaimed</span>
          <span className="dg-mop-modal__chip">{getChainLabel(OSHAR_REAL_ESTATE_CHAIN_ID)}</span>
          {hasStripe && hasWallet ? (
            <span className="dg-mop-modal__chip dg-mop-modal__chip--mixed">Mixed purchase sources</span>
          ) : hasWallet ? (
            <span className="dg-mop-modal__chip dg-mop-modal__chip--wallet">Wallet mint</span>
          ) : hasStripe ? (
            <span className="dg-mop-modal__chip dg-mop-modal__chip--card">Card purchase</span>
          ) : null}
        </div>
        <div className="dg-mop-modal__body">
          <div className="dg-mop-unit-grid">
            {units.map((u) => {
              const uContract = String(u.walletSyncContractAddress || "").trim();
              const uNft = u.status === "claimed" ? explorerNftUrl(uContract, u.tokenId) : null;
              const uBusy = claimBusyId === u.id;
              const uErr = claimErrorsMap?.[u.id] || "";
              const uWrong =
                CARD_CLAIM_MINT_ENDPOINT_AVAILABLE &&
                u.status === "paid_unminted" &&
                u.purchaseMethod === "stripe" &&
                chain?.id !== OSHAR_REAL_ESTATE_CHAIN_ID;
              const unitLabel = u.displayName || `Unit #${String(u.unitIndex || u.unitNumber || "").padStart(3, "0")}`;
              const txRaw = String(
                u.purchaseMethod === "wallet_verified" ? u.walletSyncTxHash || "" : u.claimTxHash || u.walletSyncTxHash || ""
              ).trim();
              const txHref = explorerTxUrl(txRaw);
              const walletShortModal = formatWalletShort(u.claimedWallet);
              let action = null;
              if (u.status === "claimed" && uNft) {
                action = (
                  <a href={uNft} className="dg-btn dg-btn--tiny dg-btn--gold-outline" target="_blank" rel="noreferrer">
                    View on explorer
                  </a>
                );
              } else if (u.status === "claimed" && txHref) {
                action = (
                  <a href={txHref} className="dg-btn dg-btn--tiny dg-btn--blue-outline" target="_blank" rel="noreferrer">
                    View transaction
                  </a>
                );
              } else if (u.status === "paid_unminted" && CARD_CLAIM_MINT_ENDPOINT_AVAILABLE && u.purchaseMethod === "stripe") {
                if (isStripeClaimPriceBlocked(u)) {
                  action = <span className="dg-mop-unit-tile__hint">Token price setup pending</span>;
                } else if (!isConnected || !address) {
                  action = (
                    <button
                      type="button"
                      className="dg-btn dg-btn--tiny dg-btn--blue-outline"
                      onClick={async () => {
                        try {
                          const connList = Array.isArray(connectors) ? connectors : [];
                          const connector = connList.find((c) => c && c.ready) || connList[0];
                          if (!connector) return;
                          await connectAsync({ connector });
                        } catch {
                          /* user cancelled or no wallet */
                        }
                      }}
                    >
                      Connect wallet
                    </button>
                  );
                } else {
                  action = (
                    <button
                      type="button"
                      className="dg-btn dg-btn--tiny dg-btn--blue-outline"
                      disabled={uBusy}
                      onClick={() => onStripeClaim(u)}
                    >
                      {uBusy ? "Claiming…" : uWrong ? getSwitchToNetworkLabel(OSHAR_REAL_ESTATE_CHAIN_ID) : "Claim NFT"}
                    </button>
                  );
                }
              } else if (u.status === "paid_unminted" && u.purchaseMethod === "wallet_verified") {
                action = <span className="dg-mop-unit-tile__hint">Complete wallet mint, then sync tx.</span>;
              } else if (u.status === "paid_unminted" && !CARD_CLAIM_MINT_ENDPOINT_AVAILABLE) {
                action = <span className="dg-mop-unit-tile__hint">Mint setup pending on server.</span>;
              }
              const extraLinks =
                u.status === "claimed" && uNft && txHref ? (
                  <a href={txHref} className="dg-btn dg-btn--tiny dg-btn--ghost" target="_blank" rel="noreferrer">
                    Tx
                  </a>
                ) : null;
              return (
                <div key={u.id} className="dg-mop-unit-tile">
                  <UnitTileThumb row={u} />
                  <div className="dg-mop-unit-tile__line">{unitLabel}</div>
                  <div className="dg-mop-unit-tile__meta">
                    Token #{u.tokenId} · {unitRowMintLabel(u)}
                    {u.purchaseMethod === "wallet_verified" && u.mintStatus && u.mintStatus !== "none" ? (
                      <span className="dg-mop-unit-tile__mintpill">mintStatus: {u.mintStatus}</span>
                    ) : null}
                  </div>
                  {u.purchaseMethod === "wallet_verified" ? (
                    <p className="dg-mop-unit-tile__hint dg-mop-unit-tile__hint--dense">
                      {walletShortModal ? `Wallet ${walletShortModal}` : "Wallet unavailable"}
                      {isValidTxHash(txRaw)
                        ? ` · ${shortRef(txRaw)}`
                        : !txRaw
                          ? " · Tx pending"
                          : ""}
                    </p>
                  ) : null}
                  {u.metadataStatus === "missing_unique_image" &&
                  !(
                    (u.metadataImageGatewayUrl && String(u.metadataImageGatewayUrl).trim()) ||
                    (u.imageGatewayUrl && String(u.imageGatewayUrl).trim()) ||
                    (u.metadataStatus === "assigned" &&
                      u.imageUrl &&
                      /^https?:\/\//i.test(String(u.imageUrl).trim()))
                  ) ? (
                    <p className="dg-mop-unit-tile__hint">Unique NFT image pending</p>
                  ) : null}
                  <div className="dg-mop-unit-tile__actions">
                    {action}
                    {extraLinks}
                  </div>
                  {uErr ? (
                    <p className="dg-mop-unit-tile__err" role="alert">
                      {uErr}
                    </p>
                  ) : null}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}

function detailsPaymentLabel(group) {
  const units = Array.isArray(group._unitRows) && group._unitRows.length > 0 ? group._unitRows : [group];
  const hasStripe = units.some((u) => u.purchaseMethod === "stripe");
  const hasWallet = units.some((u) => u.purchaseMethod === "wallet_verified");
  if (hasStripe && hasWallet) return "Mixed purchase sources";
  if (hasWallet && !hasStripe) return formatPurchaseMethod("wallet_verified");
  return formatPurchaseMethod(group.purchaseMethod || "stripe");
}

function OsharPropertyDetailsModal({ group, onClose }) {
  const qty = group._displayQty != null ? group._displayQty : Number(group.quantity) > 0 ? Number(group.quantity) : 1;
  const { minted, unclaimed } = groupMintCounts(group);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  const title = displayTitle(group);

  return createPortal(
    <div
      className="dg-mop-modal-overlay"
      role="presentation"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="dg-mop-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="dg-mop-details-modal-title"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="dg-mop-modal__head">
          <div>
            <h3 id="dg-mop-details-modal-title" className="dg-mop-modal__title serif">
              {title}
            </h3>
            <p className="dg-mop-modal__sub">Purchase details</p>
          </div>
          <button type="button" className="dg-mop-modal__close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>
        <div className="dg-mop-modal__body dg-mop-details-compact">
          <dl className="dg-mop-detail-dl">
            <div>
              <dt>Tier</dt>
              <dd>{group.tierSlug || "—"}</dd>
            </div>
            <div>
              <dt>Property</dt>
              <dd>{[group.assetType, group.district].filter(Boolean).join(" · ") || "—"}</dd>
            </div>
            <div>
              <dt>Quantity</dt>
              <dd>{qty}</dd>
            </div>
            <div>
              <dt>Purchased</dt>
              <dd>{formatShortWhen(group.paidAt || group.createdAt)}</dd>
            </div>
            <div>
              <dt>Payment</dt>
              <dd>{detailsPaymentLabel(group)}</dd>
            </div>
            <div>
              <dt>Registry</dt>
              <dd>{STATUS_LABELS[group.status] || group.status}</dd>
            </div>
            <div>
              <dt>Claim summary</dt>
              <dd>
                {minted} minted · {unclaimed} unclaimed
              </dd>
            </div>
            {group.purchaseMethod === "stripe" && group.stripeSessionId ? (
              <div>
                <dt>Checkout ref.</dt>
                <dd className="dg-mop-card__mono">{shortRef(group.stripeSessionId)}</dd>
              </div>
            ) : null}
          </dl>
          {group.status === "paid_unminted" && CARD_CLAIM_MINT_ENDPOINT_AVAILABLE && group.purchaseMethod === "stripe" ? (
            <p className="dg-mop-card__note" style={{ marginTop: 10 }}>
              Claim mints on <strong>{getChainLabel(OSHAR_REAL_ESTATE_CHAIN_ID)}</strong> to the wallet linked in Gateway settings (must match your connected
              address). Use <strong>View units</strong> to claim each NFT.
            </p>
          ) : null}
        </div>
      </div>
    </div>,
    document.body
  );
}

function OsharPropertyVaultCard({
  r,
  onOpenUnits,
  onOpenDetails,
  onStripeClaim,
  claimBusyId,
  claimErrorsMap,
}) {
  const { chain } = useNetwork();
  const units = Array.isArray(r._unitRows) && r._unitRows.length > 0 ? r._unitRows : [r];
  const isGroup = units.length > 1;
  const gid = r._groupId || r.id;
  const qty = r._displayQty != null ? r._displayQty : Number(r.quantity) > 0 ? Number(r.quantity) : 1;
  const qtyLabel = qty > 1 ? `×${qty}` : null;
  const first = units[0];
  const contract = String(first.walletSyncContractAddress || "").trim();
  const nftUrl = r.status === "claimed" && !isGroup ? explorerNftUrl(contract, first.tokenId) : null;

  const walletUnitCount = units.filter((u) => u.purchaseMethod === "wallet_verified").length;
  const hasStripe = units.some((u) => u.purchaseMethod === "stripe");
  const hasWallet = units.some((u) => u.purchaseMethod === "wallet_verified");

  const claimBusy = claimBusyId != null && units.some((u) => u.id === claimBusyId);
  const wrongNetwork =
    CARD_CLAIM_MINT_ENDPOINT_AVAILABLE &&
    r.status === "paid_unminted" &&
    r.purchaseMethod === "stripe" &&
    chain?.id !== OSHAR_REAL_ESTATE_CHAIN_ID;

  const groupClaimErr = units.map((u) => claimErrorsMap?.[u.id]).find((x) => x);

  let primary = null;
  if (isGroup) {
    primary = (
      <button type="button" className="dg-btn dg-btn--tiny dg-btn--blue-outline" onClick={() => onOpenUnits(gid)}>
        View units
      </button>
    );
  } else if (r.status === "claimed" && !isGroup) {
    primary = nftUrl ? (
      <a href={nftUrl} className="dg-btn dg-btn--tiny dg-btn--gold-outline dg-btn--sweep" target="_blank" rel="noreferrer">
        View on explorer <i className="fas fa-arrow-right dg-btn__icon" aria-hidden />
      </a>
    ) : (
      <Link to="/world/oshar/real-estate" className="dg-btn dg-btn--tiny dg-btn--blue-outline dg-btn--sweep">
        View property <i className="fas fa-arrow-right dg-btn__icon" aria-hidden />
      </Link>
    );
  } else if (r.status === "paid_unminted") {
    if (CARD_CLAIM_MINT_ENDPOINT_AVAILABLE && first.purchaseMethod === "stripe") {
      if (isStripeClaimPriceBlocked(first)) {
        primary = <span className="dg-mop-card__note">Token price setup pending</span>;
      } else {
        primary = (
          <button
            type="button"
            className="dg-btn dg-btn--tiny dg-btn--blue-outline"
            disabled={claimBusy}
            onClick={() => onStripeClaim(first)}
          >
            {claimBusy ? "Claiming…" : wrongNetwork ? getSwitchToNetworkLabel(OSHAR_REAL_ESTATE_CHAIN_ID) : "Claim NFT"}
          </button>
        );
      }
    } else {
      primary = (
        <button type="button" className="dg-btn dg-btn--tiny dg-btn--blue-outline" onClick={() => onOpenDetails(gid)}>
          Mint setup required
        </button>
      );
    }
  }

  const summaryLine = groupMintSummaryLine(r);

  return (
    <article className="dg-mop-card dg-mop-card--vault dg-gold-ring">
      <OsharThumb r={r} />
      <div className="dg-mop-card__body">
        <div className="dg-mop-card__body-inner">
          <div className="dg-mop-card__head">
            <span className={`dg-mop-card__badge ${badgeClassForGroup(r)}`}>{paidBadgeShort(r)}</span>
          </div>
          <h3 className="dg-mop-card__title serif">{displayTitle(r)}</h3>
          <p className="dg-mop-card__meta">
            {[r.assetType, r.district].filter(Boolean).join(" · ") || "Oshar VR"}
            {qtyLabel ? <span className="dg-mop-card__qty">{qtyLabel}</span> : null}
          </p>
          {summaryLine ? <p className="dg-mop-card__summary">{summaryLine}</p> : null}
          <WalletMintedUnitsSubsection
            units={units}
            onViewUnits={() => onOpenUnits(gid)}
            showViewUnits={walletUnitCount > WALLET_UNITS_PREVIEW_MAX}
          />
          <div className="dg-mop-card__source-chips" aria-label="Purchase source">
            {hasStripe && hasWallet ? (
              <span className="dg-mop-card__source-chip dg-mop-card__source-chip--mixed">Mixed purchase sources</span>
            ) : null}
            {hasWallet && !hasStripe ? (
              <span className="dg-mop-card__source-chip dg-mop-card__source-chip--wallet">Wallet mint</span>
            ) : null}
            {hasStripe && !hasWallet ? (
              <span className="dg-mop-card__source-chip dg-mop-card__source-chip--card">Card purchase</span>
            ) : null}
          </div>
        </div>
        <div className="dg-mop-card__actions">
          {primary}
          <button type="button" className="dg-btn dg-btn--tiny dg-btn--ghost" onClick={() => onOpenDetails(gid)}>
            Details
          </button>
        </div>
        {groupClaimErr ? (
          <p className="dg-mop-card__note dg-oshar-access__state--error" role="alert" style={{ marginTop: 8 }}>
            {groupClaimErr}
          </p>
        ) : null}
      </div>
    </article>
  );
}

function PendingCheckoutMini({ r }) {
  return (
    <div className="dg-mop-pending__row">
      <div>
        <span className="dg-mop-pending__title">{displayTitle(r)}</span>
        <span className="dg-mop-pending__badge">Incomplete checkout</span>
      </div>
      <p className="dg-mop-pending__hint">Started {formatShortWhen(r.createdAt)}</p>
      <Link to="/world/oshar/real-estate" className="dg-btn dg-btn--tiny dg-btn--ghost dg-btn--sweep">
        Continue on storefront <i className="fas fa-arrow-right dg-btn__icon" aria-hidden />
      </Link>
    </div>
  );
}

const MyOsharAccessPanel = () => {
  const { isAuthenticated, loading: authLoading, user } = useAuth();
  const location = useLocation();
  const debugPurchase = useMemo(
    () => new URLSearchParams(location.search).get("debugPurchase") === "1",
    [location.search]
  );
  const authUserKey = useMemo(() => {
    const u = user;
    if (!u || typeof u !== "object") return "";
    return String(u._id || u.id || u.username || "").trim();
  }, [user]);
  const { address, isConnected } = useAccount();
  const { chain } = useNetwork();
  const { switchNetwork } = useSwitchNetwork();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [rows, setRows] = useState([]);
  const [showAll, setShowAll] = useState(false);
  const [unitsModalId, setUnitsModalId] = useState(null);
  const [detailsModalId, setDetailsModalId] = useState(null);
  const [pendingOpen, setPendingOpen] = useState(false);
  const [syncTx, setSyncTx] = useState("");
  const [syncBusy, setSyncBusy] = useState(false);
  const [syncMsg, setSyncMsg] = useState("");
  const [claimBusyId, setClaimBusyId] = useState(null);
  const [claimErrors, setClaimErrors] = useState({});
  const [pendingMintSyncBanner, setPendingMintSyncBanner] = useState(null);
  const [pendingMintSyncBusy, setPendingMintSyncBusy] = useState(false);

  useEffect(() => {
    const rec = readPendingOsharWalletMintRecord();
    if (!rec?.txHash) {
      setPendingMintSyncBanner(null);
      return;
    }
    const pendingIds = parsePendingWalletTokenIds(rec);
    const rowTokenIds = rows
      .map((r) => Math.floor(Number(r.tokenId)))
      .filter((n) => Number.isFinite(n));
    const synced = pendingIds.some((pid) => rowTokenIds.includes(pid));
    if (synced) {
      clearPendingOsharWalletMintRecord();
      setPendingMintSyncBanner(null);
      return;
    }
    setPendingMintSyncBanner({
      txShort: `${String(rec.txHash).slice(0, 10)}…${String(rec.txHash).slice(-6)}`,
    });
  }, [rows]);

  const load = useCallback(
    async ({ signal } = {}) => {
      setLoading(true);
      setError("");
      const t0 = typeof performance !== "undefined" ? performance.now() : 0;
      // eslint-disable-next-line no-console
      console.log("[oshar-vault] fetch_begin");
      try {
        const list = await getMyOsharReservations({ debugPurchaseToBackend: debugPurchase, signal });
        logClientMyOsharReservationsDebug(list, "response");
        setRows(Array.isArray(list) ? list : []);
        if (typeof performance !== "undefined") {
          const durationMs = Math.round((performance.now() - t0) * 100) / 100;
          // eslint-disable-next-line no-console
          console.log("[oshar-vault] fetch_done", { durationMs, count: Array.isArray(list) ? list.length : 0 });
        }
      } catch (e) {
        if (e?.name === "AbortError") {
          return;
        }
        setError("Unable to load Oshar properties.");
        setRows([]);
      } finally {
        if (!signal?.aborted) {
          setLoading(false);
        }
      }
    },
    [debugPurchase]
  );

  useEffect(() => {
    // eslint-disable-next-line no-console
    console.log("[oshar-vault] mount");
  }, []);

  useEffect(() => {
    if (authLoading) return undefined;
    if (!isAuthenticated) {
      setRows([]);
      setError("");
      setLoading(false);
      return undefined;
    }
    const ac = new AbortController();
    load({ signal: ac.signal });
    return () => ac.abort();
  }, [authLoading, isAuthenticated, authUserKey, debugPurchase, load]);

  const handleStripeClaim = useCallback(
    async (u) => {
      const rid = u.id;
      if (!isAuthenticated) {
        setClaimErrors((prev) => ({ ...prev, [rid]: "Please log in to claim." }));
        return;
      }
      if (!isConnected || !address) {
        setClaimErrors((prev) => ({
          ...prev,
          [rid]: "Connect your Ethereum wallet (same address you linked in Gateway settings).",
        }));
        return;
      }
      if (chain?.id !== OSHAR_REAL_ESTATE_CHAIN_ID) {
        try {
          if (switchNetwork) await switchNetwork(OSHAR_REAL_ESTATE_CHAIN_ID);
        } catch {
          setClaimErrors((prev) => ({
            ...prev,
            [rid]: `Switch your wallet to ${getChainLabel(OSHAR_REAL_ESTATE_CHAIN_ID)} (chain ${OSHAR_REAL_ESTATE_CHAIN_ID}), then try again.`,
          }));
        }
        return;
      }
      if (isStripeClaimPriceBlocked(u)) {
        setClaimErrors((prev) => ({
          ...prev,
          [rid]: "Token price setup pending",
        }));
        return;
      }
      setClaimBusyId(rid);
      setClaimErrors((prev) => ({ ...prev, [rid]: "" }));
      try {
        await claimOsharStripeReservation({ reservationId: rid, walletAddress: address });
        await load();
      } catch (e) {
        const raw = e?.message || "Claim failed.";
        const lower = String(raw).toLowerCase();
        const msg =
          lower.includes("tokenethprice is zero") || lower.includes("price is not set")
            ? "Token price setup pending"
            : raw;
        setClaimErrors((prev) => ({ ...prev, [rid]: msg }));
      } finally {
        setClaimBusyId(null);
      }
    },
    [address, chain?.id, isAuthenticated, isConnected, load, switchNetwork]
  );

  const runWalletSync = useCallback(async () => {
    if (!isAuthenticated) {
      setSyncMsg("Log in to verify and save a wallet mint.");
      return;
    }
    setSyncBusy(true);
    setSyncMsg("");
    try {
      await syncOsharWalletMint({ txHash: syncTx.trim(), chainId: OSHAR_REAL_ESTATE_CHAIN_ID });
      clearPendingOsharWalletMintRecord();
      setSyncMsg(`Verified on ${getChainLabel(OSHAR_REAL_ESTATE_CHAIN_ID)} and saved to your registry.`);
      setSyncTx("");
      await load();
    } catch (e) {
      setSyncMsg(e?.message || "Verification failed.");
    } finally {
      setSyncBusy(false);
    }
  }, [isAuthenticated, load, syncTx]);

  const runPendingMintRetry = useCallback(async () => {
    if (!isAuthenticated) {
      setSyncMsg("Log in to retry dashboard sync.");
      return;
    }
    setPendingMintSyncBusy(true);
    setSyncMsg("");
    try {
      await retryPendingOsharWalletSync();
      setPendingMintSyncBanner(null);
      setSyncMsg("Pending wallet mint synced to your registry.");
      await load();
    } catch (e) {
      setSyncMsg(String(e?.message || e || "Retry failed."));
    } finally {
      setPendingMintSyncBusy(false);
    }
  }, [isAuthenticated, load]);

  const { owned, pending, closed } = useMemo(() => {
    const t0 = typeof performance !== "undefined" ? performance.now() : 0;
    const result = partitionRows(rows);
    if (!loading && typeof performance !== "undefined") {
      const durationMs = Math.round((performance.now() - t0) * 100) / 100;
      const count = result.owned.length + result.pending.length + result.closed.length;
      // eslint-disable-next-line no-console
      console.log("[oshar-vault] render_groups_done", { durationMs, count });
    }
    return result;
  }, [rows, loading]);
  const visibleOwned = showAll ? owned : owned.slice(0, 8);
  const hasMoreOwned = owned.length > 8;

  const recentWalletMinted = useMemo(
    () =>
      [...rows]
        .filter((r) => r.purchaseMethod === "wallet_verified" && r.status === "claimed")
        .sort(
          (a, b) =>
            ts(b.walletVerifiedAt || b.mintedAt || b.createdAt) -
            ts(a.walletVerifiedAt || a.mintedAt || a.createdAt)
        )
        .slice(0, 8),
    [rows]
  );

  const unitsModalGroup = useMemo(
    () => (unitsModalId ? owned.find((x) => (x._groupId || x.id) === unitsModalId) : null),
    [owned, unitsModalId]
  );
  const detailsModalGroup = useMemo(
    () => (detailsModalId ? owned.find((x) => (x._groupId || x.id) === detailsModalId) : null),
    [owned, detailsModalId]
  );

  return (
    <section
      id="oshar-access"
      className="dg-mop dg-oshar-access dg-oshar-access--compact dg-gold-ring dg-premium-ring"
      aria-labelledby="dg-mop-heading"
    >
      <div className="dg-mop__header">
        <div className="dg-oshar-access__brand">
          <span className="dg-oshar-access__deed" aria-hidden>
            <i className="fas fa-city" />
          </span>
          <div className="dg-oshar-access__titles">
            <h2 id="dg-mop-heading" className="dg-mop__h serif">
              My Oshar Properties
            </h2>
            <p className="dg-mop__sub dg-label-up dg-muted">Purchased property access, claim status, and wallet minting.</p>
          </div>
        </div>
        {hasMoreOwned ? (
          <button type="button" className="dg-btn dg-btn--tiny dg-btn--blue-outline" onClick={() => setShowAll((v) => !v)}>
            {showAll ? "Show fewer" : `View all (${owned.length})`}
          </button>
        ) : null}
      </div>

      {loading && (
        <p className="dg-oshar-access__state dg-hud" role="status">
          Loading your Oshar vault…
        </p>
      )}

      {!loading && error && (
        <p className="dg-oshar-access__state dg-oshar-access__state--error" role="alert">
          {error}
        </p>
      )}

      {!loading && !error && debugPurchase ? (
        <p className="dg-mop-debug-line dg-hud" role="note">
          Token #100017 in dashboard response: {rows.some((r) => Number(r.tokenId) === 100017) ? "yes" : "no"}
        </p>
      ) : null}

      {!loading && !error && pendingMintSyncBanner && (
        <div className="dg-oshar-access__state dg-mop-pending-sync" role="status" style={{ marginBottom: 12 }}>
          <p className="dg-mop-pending-sync__title">Pending sync</p>
          <p style={{ margin: "0 0 8px" }}>
            Your last wallet mint is not reflected in the dashboard yet
            {pendingMintSyncBanner.txShort ? ` (${pendingMintSyncBanner.txShort})` : ""}. Retry sync or open{" "}
            <strong>Sync existing wallet mint</strong> below.
          </p>
          <button
            type="button"
            className="dg-btn dg-btn--tiny dg-btn--gold-outline"
            disabled={pendingMintSyncBusy}
            onClick={runPendingMintRetry}
          >
            {pendingMintSyncBusy ? "Retrying…" : "Retry Dashboard Sync"}
          </button>
        </div>
      )}

      {!loading && !error && rows.length === 0 && (
        <div className="dg-oshar-access__inline-empty">
          <span className="dg-oshar-access__inline-empty-txt">No Oshar properties yet.</span>
          <Link to="/world/oshar/real-estate" className="dg-btn dg-btn--tiny dg-btn--gold-outline dg-btn--sweep">
            Explore Oshar Real Estate <i className="fas fa-arrow-right dg-btn__icon" aria-hidden />
          </Link>
        </div>
      )}

      {!loading && !error && recentWalletMinted.length > 0 ? (
        <div className="dg-mop-wallet-strip" role="region" aria-label="Recently wallet minted">
          <span className="dg-mop-wallet-strip__label">Recently wallet minted</span>
          <div className="dg-mop-wallet-strip__chips">
            {recentWalletMinted.map((r) => (
              <span key={r.id} className="dg-mop-wallet-strip__chip">
                #{r.tokenId}
              </span>
            ))}
          </div>
        </div>
      ) : null}

      {!loading && !error && visibleOwned.length > 0 && (
        <div className="dg-mop__rail dg-mop__rail--vault">
          {visibleOwned.map((r) => (
            <OsharPropertyVaultCard
              key={r._groupId || r.id}
              r={r}
              onOpenUnits={(id) => {
                setDetailsModalId(null);
                setUnitsModalId(id);
              }}
              onOpenDetails={(id) => {
                setUnitsModalId(null);
                setDetailsModalId(id);
              }}
              onStripeClaim={handleStripeClaim}
              claimBusyId={claimBusyId}
              claimErrorsMap={claimErrors}
            />
          ))}
        </div>
      )}

      {unitsModalGroup ? (
        <OsharPropertyUnitsModal
          group={unitsModalGroup}
          onClose={() => setUnitsModalId(null)}
          onStripeClaim={handleStripeClaim}
          claimBusyId={claimBusyId}
          claimErrorsMap={claimErrors}
        />
      ) : null}

      {detailsModalGroup ? (
        <OsharPropertyDetailsModal group={detailsModalGroup} onClose={() => setDetailsModalId(null)} />
      ) : null}

      {!loading && !error && pending.length > 0 && (
        <div className="dg-mop-pending-wrap">
          <button
            type="button"
            className="dg-mop-pending-toggle"
            onClick={() => setPendingOpen((v) => !v)}
            aria-expanded={pendingOpen}
          >
            <span>Pending checkouts</span>
            <span className="dg-mop-pending-count" aria-hidden>
              {pending.length}
            </span>
          </button>
          {pendingOpen ? (
            <div className="dg-mop-pending" aria-labelledby="dg-mop-pending-h">
              <h3 id="dg-mop-pending-h" className="dg-mop-pending__h">
                Incomplete checkouts
              </h3>
              <p className="dg-mop-pending__lead">Not paid yet — separate from owned properties.</p>
              <div className="dg-mop-pending__list">
                {pending.map((r) => (
                  <PendingCheckoutMini key={r.id} r={r} />
                ))}
              </div>
            </div>
          ) : null}
        </div>
      )}

      {!loading && !error && closed.length > 0 && (
        <p className="dg-mop-closed-hint" role="note">
          {closed.length} expired or cancelled record{closed.length !== 1 ? "s" : ""} hidden — not active ownership.
        </p>
      )}

      <details className="dg-mop-sync">
        <summary className="dg-mop-sync__summary">Sync existing wallet mint</summary>
        <div className="dg-mop-sync__body">
          <p className="dg-muted" style={{ fontSize: 12, marginBottom: 8, maxWidth: 640 }}>
            Paste a transaction hash from {getChainLabel(OSHAR_REAL_ESTATE_CHAIN_ID)} after minting with MetaMask. Treydora verifies the receipt on-chain.
            Link the same wallet to your account in Gateway settings first.
            {isConnected && address ? (
              <span>
                {" "}
                Connected: {address.slice(0, 6)}…{address.slice(-4)}
              </span>
            ) : null}
          </p>
          <div className="dg-mop-sync__row">
            <input
              type="text"
              placeholder="0x… transaction hash"
              value={syncTx}
              onChange={(e) => setSyncTx(e.target.value)}
              disabled={syncBusy}
              className="dg-mop-sync__input"
              aria-label="Transaction hash"
            />
            <button type="button" className="dg-btn dg-btn--tiny dg-btn--gold-outline" disabled={syncBusy} onClick={runWalletSync}>
              {syncBusy ? "Verifying…" : "Verify & sync"}
            </button>
          </div>
          {syncMsg ? (
            <p className="dg-oshar-access__state" role="status" style={{ marginTop: 8 }}>
              {syncMsg}
            </p>
          ) : null}
        </div>
      </details>
    </section>
  );
};

export default MyOsharAccessPanel;
